// The Map view's questions of the structure, and the boundary editor's
// gestures as plain functions (handoff §7g, "Map view"). MapCanvas turns
// clicks and drags into these calls; the page holds the result. The server
// validates every saved shape again (services/boundaries.js); the only check
// made here is the one a person can act on while drawing: three distinct
// points before Save.

import { StructureBoundary, StructureNode, StructureSite, childrenOf, isActiveRow } from './structure'
import { plural } from '../wages/format'

export interface LatLng {
  lat: number
  lng: number
}

export type EditMode = 'draw' | 'reshape'

export interface Edit {
  nodeId: string
  mode: EditMode
  // The boundary the editor opened (sent as `before`), or null for a draw.
  before: string | null
  points: LatLng[]
  // The points it opened with, to tell an unsaved change from none.
  start: LatLng[]
}

// The nodes that can carry a boundary: active ones at the mapping level, in
// tree order. "Only nodes at the chosen level can carry a polygon."
export function mapTargets(nodes: StructureNode[], site: Pick<StructureSite, 'map_level'>): StructureNode[] {
  const byId = new Map(nodes.map((x) => [x.id, x]))
  const live = new Set<string>()
  for (const n of nodes) {
    if (!isActiveRow(n)) continue
    for (let p: StructureNode | undefined = n; p; p = p.parent_id ? byId.get(p.parent_id) : undefined) live.add(p.id)
  }
  const out: StructureNode[] = []
  const walk = (parent: string | null) => {
    for (const n of childrenOf(nodes, parent)) {
      if (n.level === site.map_level) {
        if (live.has(n.id)) out.push(n)
      } else if (n.level < site.map_level) walk(n.id)
    }
  }
  walk(null)
  return out
}

// "coverage reads '18 of 24 rows mapped'".
export function coverageLine(targets: StructureNode[], boundaries: StructureBoundary[], label: string): string {
  const drawn = new Set(boundaries.map((b) => b.node_id))
  const mapped = targets.filter((t) => drawn.has(t.id)).length
  const noun = label.toLowerCase()
  return `${mapped} of ${plural(targets.length, noun, `${noun}s`)} mapped`
}

const same = (a: LatLng, b: LatLng) => a.lat === b.lat && a.lng === b.lng
export const round6 = (p: LatLng): LatLng => ({ lat: Number(p.lat.toFixed(6)), lng: Number(p.lng.toFixed(6)) })

export function distinctPoints(points: LatLng[]): number {
  const seen: LatLng[] = []
  for (const p of points.map(round6)) if (!seen.some((q) => same(p, q))) seen.push(p)
  return seen.length
}

export function startDraw(nodeId: string): Edit {
  return { nodeId, mode: 'draw', before: null, points: [], start: [] }
}

export function startReshape(b: StructureBoundary): Edit {
  return { nodeId: b.node_id, mode: 'reshape', before: b.id, points: b.points.map((p) => ({ ...p })), start: b.points }
}

export const isDirty = (e: Edit) => e.points.length !== e.start.length || e.points.some((p, i) => !same(round6(p), round6(e.start[i])))

// Left-click while drawing places a point at the end of the outline.
export function addPoint(e: Edit, p: LatLng): Edit {
  return { ...e, points: [...e.points, round6(p)] }
}

// Dragging a vertex moves it.
export function movePoint(e: Edit, i: number, p: LatLng): Edit {
  return { ...e, points: e.points.map((q, j) => (j === i ? round6(p) : q)) }
}

// Dragging the midpoint of the edge from point i to the next one adds a
// point between them, where it was dropped.
export function insertPoint(e: Edit, i: number, p: LatLng): Edit {
  const points = [...e.points]
  points.splice(i + 1, 0, round6(p))
  return { ...e, points }
}

export const MIN_POINTS = 3
export const DELETE_BLOCKED = 'A boundary needs at least three points, so this one cannot be removed. Drag it instead.'

// Double-clicking a vertex deletes it, "blocked at three, with the reason".
export function deletePoint(e: Edit, i: number): { edit: Edit; refused?: string } {
  if (e.points.length === MIN_POINTS) return { edit: e, refused: DELETE_BLOCKED }
  return { edit: { ...e, points: e.points.filter((_, j) => j !== i) } }
}

// The midpoints of the outline's edges, each with the index of the point it
// follows. The closing edge counts once the outline is a polygon.
export function midpoints(points: LatLng[]): { after: number; at: LatLng }[] {
  const out: { after: number; at: LatLng }[] = []
  const n = points.length
  const edges = n >= MIN_POINTS ? n : n - 1
  for (let i = 0; i < edges; i++) {
    const a = points[i]
    const b = points[(i + 1) % n]
    out.push({ after: i, at: { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 } })
  }
  return out
}

// Why Save is not offered yet, or null when it is.
export function saveBlocked(e: Edit): string | null {
  const d = distinctPoints(e.points)
  if (d < MIN_POINTS) return `Place at least three points (${d} so far)`
  if (!isDirty(e)) return 'Nothing has changed'
  return null
}

// Where the map opens: the Site's centre and zoom, else around its drawn
// boundaries, else nowhere in particular (the world). A map is never opened
// on a guessed place.
export type Opening = { kind: 'centre'; centre: LatLng; zoom: number } | { kind: 'fit'; points: LatLng[] } | { kind: 'world' }

export const DEFAULT_ZOOM = 16

export function openingView(site: { map_centre?: LatLng | null; map_zoom?: number | null }, boundaries: StructureBoundary[]): Opening {
  if (site.map_centre) return { kind: 'centre', centre: site.map_centre, zoom: site.map_zoom ?? DEFAULT_ZOOM }
  const points = boundaries.flatMap((b) => b.points)
  if (points.length) return { kind: 'fit', points }
  return { kind: 'world' }
}
